import { printscreen } from '@/utils/screen/printscreen'
import { computed, onMounted, onUnmounted, ref } from 'vue'

export interface ShareOption {
  id: string
  label: string
  selected: boolean
}

export interface UseSharePopoverOptions {
  captureSelector: string
  captureFileName: string
  onGenerateCSV?: () => void
}

export function useSharePopover(options: UseSharePopoverOptions) {
  // Refs
  const isPopoverOpen = ref(false)
  const isLoadingShareButton = ref(false)
  const selectedOptions = ref<Record<string, boolean>>({})

  // Computed
  const hasSelectedOptions = computed(() => {
    return Object.values(selectedOptions.value).some((selected) => selected)
  })

  // Métodos
  const togglePopover = () => {
    isPopoverOpen.value = !isPopoverOpen.value
  }

  const closePopover = () => {
    isPopoverOpen.value = false
  }

  const captureCharts = () => {
    isLoadingShareButton.value = true

    closePopover()

    // Pequeno delay para garantir que o popover foi fechado antes da captura
    setTimeout(() => {
      printscreen({
        name: options.captureFileName,
        querySelector: options.captureSelector,
      }).finally(() => {
        isLoadingShareButton.value = false
      })
    }, 100)
  }

  const generateCSV = () => {
    if (options.onGenerateCSV) {
      options.onGenerateCSV()
    }
    closePopover()
  }

  // Event listeners para fechar o popover com ESC
  const handleKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && isPopoverOpen.value) {
      closePopover()
    }
  }

  onMounted(() => {
    document.addEventListener('keydown', handleKeydown)
  })

  onUnmounted(() => {
    document.removeEventListener('keydown', handleKeydown)
  })

  return {
    // State
    isPopoverOpen,
    isLoadingShareButton,
    selectedOptions,

    // Computed
    hasSelectedOptions,

    // Methods
    togglePopover,
    closePopover,
    captureCharts,
    generateCSV,
  }
}
